import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppTheme } from './comfort';

export type { AppTheme };

export const THEMES: Array<{ id: AppTheme; label: string; hint: string }> = [
  { id: 'paper', label: 'Paper', hint: 'Warm editorial light' },
  { id: 'dusk', label: 'Dusk', hint: 'Dark warm low-glare' },
  { id: 'oled', label: 'OLED', hint: 'True black, max contrast' }
];

interface ThemeContextValue {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const storageKey = 'cp-ultimate-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<AppTheme>(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === 'oled' || saved === 'dusk' ? saved : 'paper';
  });
  const setTheme = (next: AppTheme) => {
    setThemeState(next);
    localStorage.setItem(storageKey, next);
  };
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === 'paper' ? 'light' : 'dark';
  }, [theme]);
  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    setTheme,
    cycleTheme: () => setTheme(theme === 'paper' ? 'dusk' : theme === 'dusk' ? 'oled' : 'paper')
  }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside ThemeProvider.');
  return value;
}
